import AxiosInstance from '@/api/axios';
import {
  defaultPagination,
  PaginationInterface,
} from '@/components/Base/Pagination';
import { GeneralFilterType } from '@/utils/types';
import { downloadCSV } from '@/utils/helper';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export interface SalesDataType {
  sender: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  source_group: string;
  source_name: string;
  category: 'Agent Managed' | 'Agent Assisted';
  branchName: string;
  created_date: string;
  leadFrom: string;
}

interface SalesFilterType extends GeneralFilterType {
  search?: string | null;
  sort?: string | null;
  order?: string | null;
  currentPage?: number;
  perpage?: number;
  pageLimit: string;
}

let abortController: AbortController | null = null;

export const fetchSales = createAsyncThunk(
  'fetchSales/fetchData',
  async (
    {
      organization_id,
      agent,
      branch_id,
      region_id,
      date_range,
      search,
      currentPage,
      perpage,
      pageLimit,
      sort,
      order,
      reengaged,
    }: SalesFilterType,
    thunkAPI
  ) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    try {
      const response = await AxiosInstance.get(`/leads/sales`, {
        params: {
          page: currentPage,
          perpage,
          organization_id,
          branch_id,
          region_id,
          date_range,
          pageLimit,
          agent,
          search,
          sort,
          order,
          reengaged,
        },
        signal: abortController.signal,
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error: any) {
      if (!abortController.signal.aborted || error.code === 'ERR_CANCELED') {
        return null;
      }
      return thunkAPI.rejectWithValue(error);
    } finally {
      abortController = null;
    }
  }
);

export const exportSales = createAsyncThunk(
  'fetchSales/exportSales',
  async (
    {
      organization_id,
      agent,
      branch_id,
      region_id,
      date_range,
      search,
      reengaged,
    }: SalesFilterType & { reengaged: string[] },
    thunkAPI
  ) => {
    try {
      const response = await AxiosInstance.get(`/leads/sales`, {
        params: {
          organization_id,
          branch_id,
          region_id,
          date_range,
          agent,
          search,
          exportAll: true,
          reengaged,
        },
      });

      if (response.data.success) {
        const rows: SalesDataType[] = response.data.data?.data || [];
        downloadCSV(
          rows.map((row) => ({
            name: `${row.first_name || ''}${row.last_name && row.last_name !== 'N/A'
                ? ` ${row.last_name}`
                : ''
              }`.trim(),
            email: row.email || '',
            phone: row.phone || '',
            branchName: row.branchName || '',
          })),
          [
            { key: 'name', label: 'Name' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone' },
            { key: 'branchName', label: 'Location' },
          ],
          `sales-${new Date().toISOString().slice(0, 10)}.csv`
        );
      }
      return null;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

interface SalesStateType {
  sales: {
    data: SalesDataType[] | null | undefined;
    pagination: PaginationInterface;
    showLeadFromColumn: boolean;
  };
  loading: boolean;
  exportLoading: boolean;
  error: string | null;
}

const initialState: SalesStateType = {
  sales: {
    data: null,
    pagination: defaultPagination,
    showLeadFromColumn: false,
  },
  loading: false,
  exportLoading: false,
  error: null,
};

const salesReducer = createSlice({
  name: 'sales',
  initialState,
  reducers: {
    updatePagination: (state, action) => {
      state.sales.pagination = {
        ...state.sales.pagination,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSales.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.loading = false;
        state.sales.data = action.payload.data;
        state.sales.pagination = action.payload.pagination;
        state.sales.showLeadFromColumn =
          action.payload.showLeadFromColumn || false;
      })
      .addCase(fetchSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(exportSales.pending, (state) => {
        state.exportLoading = true;
      })
      .addCase(exportSales.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportSales.rejected, (state) => {
        state.exportLoading = false;
      });
  },
});

export const { updatePagination } = salesReducer.actions;

export default salesReducer.reducer;
